import { useState, useEffect, useCallback } from 'react';
import { emptyResumeData } from '../data/mock';

const STORAGE_KEY = 'curriculo_data';

export const useResume = () => {
  const [resumeData, setResumeData] = useState(emptyResumeData);
  const [isSaved, setIsSaved] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  // Carregar dados do localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setResumeData(parsed);
        setLastSaved(new Date());
      } catch (e) {
        console.error('Erro ao carregar dados salvos:', e);
      }
    }
  }, []);

  // Salvar automaticamente no localStorage
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(resumeData));
      setIsSaved(true);
      setLastSaved(new Date());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [resumeData]);

  const updatePersonalInfo = useCallback((field, value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
  }, []);

  const updateObjective = useCallback((value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      objective: value
    }));
  }, []);

  const addExperience = useCallback((experience) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      experiences: [...prev.experiences, { ...experience, id: Date.now().toString() }]
    }));
  }, []);

  const updateExperience = useCallback((id, field, value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      experiences: prev.experiences.map(exp =>
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    }));
  }, []);

  const removeExperience = useCallback((id) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      experiences: prev.experiences.filter(exp => exp.id !== id)
    }));
  }, []);

  const addEducation = useCallback((education) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      education: [...prev.education, { ...education, id: Date.now().toString() }]
    }));
  }, []);

  const updateEducation = useCallback((id, field, value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      education: prev.education.map(edu =>
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    }));
  }, []);

  const removeEducation = useCallback((id) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      education: prev.education.filter(edu => edu.id !== id)
    }));
  }, []);

  const addSkill = useCallback((skill) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      skills: [...prev.skills, { ...skill, id: Date.now().toString() }]
    }));
  }, []);

  const updateSkill = useCallback((id, field, value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.map(skill =>
        skill.id === id ? { ...skill, [field]: value } : skill
      )
    }));
  }, []);

  const removeSkill = useCallback((id) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill.id !== id)
    }));
  }, []);

  const addLanguage = useCallback((language) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      languages: [...prev.languages, { ...language, id: Date.now().toString() }]
    }));
  }, []);

  const updateLanguage = useCallback((id, field, value) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      languages: prev.languages.map(lang =>
        lang.id === id ? { ...lang, [field]: value } : lang
      )
    }));
  }, []);

  const removeLanguage = useCallback((id) => {
    setIsSaved(false);
    setResumeData(prev => ({
      ...prev,
      languages: prev.languages.filter(lang => lang.id !== id)
    }));
  }, []);

  const clearAllData = useCallback(() => {
    setResumeData(emptyResumeData);
    localStorage.removeItem(STORAGE_KEY);
    setIsSaved(true);
  }, []);

  const loadMockData = useCallback((mockData) => {
    setResumeData(mockData);
    setIsSaved(false);
  }, []);

  return {
    resumeData,
    isSaved,
    lastSaved,
    updatePersonalInfo,
    updateObjective,
    addExperience,
    updateExperience,
    removeExperience,
    addEducation,
    updateEducation,
    removeEducation,
    addSkill,
    updateSkill,
    removeSkill,
    addLanguage,
    updateLanguage,
    removeLanguage,
    clearAllData,
    loadMockData
  };
};
